import ConsensusBoard from './ConsensusBoard';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Tooltip, ToggleButtonGroup, ToggleButton,
  TextField, InputAdornment, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
} from '@mui/material';

// ── Constants ────────────────────────────────────────────────────────────────
const ESPN_TRANSACTIONS = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl/transactions?limit=500';
const LOGO = (abbr) => abbr ? `https://a.espncdn.com/i/teamlogos/nfl/500/${abbr.toLowerCase()}.png` : null;

// eslint-disable-next-line no-unused-vars
const KEY_DATES = [
  { label: 'Legal Tampering', date: 'Mar 10', done: false },
  { label: 'New League Year', date: 'Mar 12', done: false },
  { label: 'FA Opens', date: 'Mar 12', done: false },
];

// ── Colour / design tokens ───────────────────────────────────────────────────
const CARD = { backgroundColor: '#131318', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 2, overflow: 'hidden' };
const TH = {
  backgroundColor: '#0e0e12', color: '#ffffff', fontWeight: 700, fontSize: '0.68rem',
  letterSpacing: '0.06em', textTransform: 'uppercase', py: 0.9,
  borderBottom: '1px solid rgba(245,166,35,0.20)', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 2,
};
const SCROLLBAR = {
  scrollbarWidth: 'thin', scrollbarColor: 'rgba(245,166,35,0.3) transparent',
  '&::-webkit-scrollbar': { width: 4, height: 4 },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.3)', borderRadius: 8 },
};

// ── Team logo helpers ─────────────────────────────────────────────────────────
function TeamLogo({ abbr, size = 28 }) {
  if (!abbr) return null;
  return (
    <Box component="img" src={LOGO(abbr)} alt={abbr}
      sx={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      onError={e => { e.target.style.display = 'none'; }} />
  );
}

function FaBadge({ label = 'FA', color = '#f5a623' }) {
  return (
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color, letterSpacing: '0.04em', flexShrink: 0 }}>
      {label}
    </Typography>
  );
}

// ── Parse ESPN transaction description ───────────────────────────────────────
const POS_ABBR_RE = /^(QB|WR|RB|TE|OT|OG|C|DE|DT|LB|CB|S|K|P|LS|FB|G|T|NT|ILB|OLB|EDGE|SAF|DB|FS|SS|IOL|DL|OL)s?\s+/i;
const POS_WORD_RE = /^(quarterback|wide receiver|running back|tight end|offensive tackle|offensive guard|center|defensive end|defensive tackle|linebacker|cornerback|safety|kicker|punter|guard|tackle)\s+/i;

const POS_ABBR_TRAIL_RE = /\s+(QB|WR|RB|TE|OT|OG|C|DE|DT|LB|CBs?|S|K|P|LS|FB|G|T|NT|ILB|OLB|EDGE|SAF|DB|FS|SS|IOL|DL|OL)$/i;

function stripPos(name) {
  return (name || '').replace(POS_ABBR_RE, '').replace(POS_WORD_RE, '').replace(POS_ABBR_TRAIL_RE, '').trim();
}

// Splits "Player A, Player B and Player C" into separate cleaned names
function splitPlayers(raw) {
  // First split on " and ", then split each chunk on commas
  return raw
    .split(/\s+and\s+/i)
    .flatMap(chunk => chunk.split(/,\s*/))
    .map(stripPos)
    .filter(n => n.length > 2 && /[A-Z]/.test(n));
}

function classifyTransactions(txn) {
  const desc = txn.description || '';
  const teamAbbr = txn.team?.abbreviation || '';
  const d = desc.toLowerCase();

  const makeRows = (type, rawPlayer, extra = {}) =>
    splitPlayers(rawPlayer).map(player => ({ type, player, ...extra, desc }));

  const tradeMatch = desc.match(/Traded?\s+(.+?)\s+to\s+the\s+(.+?)[.,]/i)
    || desc.match(/Traded?\s+(.+?)\s+to\s+(.+?)[.,]/i);
  if (tradeMatch) return makeRows('trade', tradeMatch[1], { fromAbbr: teamAbbr, toTeam: tradeMatch[2] });

  if (d.includes('re-signed') || d.includes('renewed')) {
    const m = desc.match(/(?:Re-signed|Renewed)\s+(.+?)\s+to\s/i);
    return m ? makeRows('resign', m[1], { fromAbbr: '', toAbbr: teamAbbr }) : [];
  }
  if (d.includes('signed')) {
    const m = desc.match(/Signed\s+(.+?)\s+to\s/i);
    return m ? makeRows('signed', m[1], { fromAbbr: '', toAbbr: teamAbbr }) : [];
  }
  if (d.includes('released') || d.includes('waived')) {
    const m = desc.match(/(?:Released|Waived)\s+(.+?)[.,]/i);
    return m ? makeRows('released', m[1], { fromAbbr: teamAbbr, toAbbr: '' }) : [];
  }
  return [];
}

const TYPE_LABELS = {
  trade:   { label: 'TRADE',     color: '#06b6d4' },
  signed:  { label: 'SIGNED',    color: '#10b981' },
  resign:  { label: 'RE-SIGNED', color: '#2ea84b' },
  released:{ label: 'RELEASED',  color: '#f5a623' },
};

// ── Recent Moves Feed ─────────────────────────────────────────────────────────
function RecentMoves() {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchTransactions = (isInitial = false) => {
    fetch(ESPN_TRANSACTIONS)
      .then(r => r.json())
      .then(data => {
        const parsed = (data.transactions || [])
          .flatMap(txn => {
            const rows = classifyTransactions(txn);
            return rows.map((r, i) => ({ ...r, date: txn.date?.slice(0,10) || '', id: (txn.id || Math.random()) + '_' + i }));
          })
          .filter(m => m.player?.length > 2);
        // Silent update — only change state if data actually changed
        setMoves(prev => {
          const prevKey = prev.map(m => m.id).join(',');
          const nextKey = parsed.map(m => m.id).join(',');
          return prevKey === nextKey ? prev : parsed;
        });
        if (isInitial) setLoading(false);
      })
      .catch(() => { if (isInitial) setLoading(false); });
  };

  useEffect(() => {
    fetchTransactions(true);
    const interval = setInterval(() => fetchTransactions(false), 5 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = filter === 'all' ? moves : moves.filter(m => m.type === filter);

  return (
    <Box sx={{ ...CARD }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(245,166,35,0.15)', backgroundColor: '#0e0e12', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', color: '#f5a623', textTransform: 'uppercase' }}>
          🔄 Recent Moves
        </Typography>
        <ToggleButtonGroup value={filter} exclusive onChange={(_,v)=>v&&setFilter(v)} size="small" sx={{ ml: 'auto' }}>
          {['all','trade','signed','resign','released'].map(f => (
            <ToggleButton key={f} value={f} sx={{
              color: filter===f ? '#f5a623' : '#64748b', borderColor: 'rgba(245,166,35,0.2)',
              backgroundColor: filter===f ? 'rgba(245,166,35,0.08)' : 'transparent',
              fontWeight: 800, fontSize: '0.6rem', px: 1, py: 0.3, textTransform: 'uppercase',
              '&:hover': { backgroundColor: 'rgba(245,166,35,0.06)' },
            }}>
              {f === 'all' ? 'All' : TYPE_LABELS[f]?.label || f}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Move list */}
      <Box sx={{ overflowY: 'auto', ...SCROLLBAR }}>
        {loading ? (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: '#f5a623' }} /></Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: '#ffffff', fontSize: '0.8rem' }}>No moves found</Typography>
        ) : filtered.map((move, i) => {
          const typeInfo = TYPE_LABELS[move.type] || { label: move.type?.toUpperCase(), color: '#94a3b8' };
          return (
            <Box key={move.id} component="a"
              href={`https://www.google.com/search?q=${encodeURIComponent(move.player)}`}
              target="_blank" rel="noopener noreferrer"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 0.9,
                borderBottom: '1px solid rgba(245,166,35,0.06)', textDecoration: 'none',
                '&:hover': { backgroundColor: 'rgba(245,166,35,0.08)', cursor: 'pointer' },
              }}>
                {/* Type chip */}
                <Chip label={typeInfo.label} size="small" sx={{
                  height: 16, fontSize: '0.52rem', fontWeight: 900, flexShrink: 0,
                  bgcolor: typeInfo.color + '18', color: typeInfo.color, border: `1px solid ${typeInfo.color}44`,
                  minWidth: 64,
                }} />

                {/* Player name */}
                <Typography sx={{ flex: 1, fontSize: '0.8rem', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {move.player}
                </Typography>

                {/* From → To logos */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  {move.fromAbbr
                    ? <TeamLogo abbr={move.fromAbbr} size={24} />
                    : move.type === 'resigned' && <FaBadge size={24} />
                  }
                  {move.fromAbbr && (
                    <Typography sx={{ color: '#ffffff', fontSize: '1rem', fontWeight: 900 }}>→</Typography>
                  )}
                  {move.toAbbr
                    ? <TeamLogo abbr={move.toAbbr} size={24} />
                    : (move.type === 'released' || (move.type === 'trade' && !move.toAbbr))
                      ? <FaBadge label="FA" size={24} />
                      : null
                  }
                </Box>

                {/* Date */}
                <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', flexShrink: 0, ml: 0.5 }}>
                  {move.date?.slice(5)}
                </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Remaining Free Agents Table ───────────────────────────────────────────────
const FA_POS = ['ALL', 'QB', 'WR', 'RB', 'TE', 'OT', 'DE', 'LB', 'CB', 'S'];
let _faCache = null;
let _signedCache = null; // Set of lowercased player names who are signed/traded

// eslint-disable-next-line no-unused-vars
function RemainingFreeAgents() {
  const [all, setAll] = useState([]);
  const [signedNames, setSignedNames] = useState(_signedCache || new Set());
  const [pos, setPos] = useState('ALL');
  const [orderBy, setOrderBy] = useState('nerdRating');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load FA list
    const faPromise = _faCache
      ? Promise.resolve(_faCache)
      : fetch('/data/free_agents_2026.json').then(r => r.json()).then(d => { _faCache = d.players; return d.players; });

    // Load ESPN transactions to find signed/traded players
    const txnPromise = _signedCache
      ? Promise.resolve(_signedCache)
      : fetch(ESPN_TRANSACTIONS)
          .then(r => r.json())
          .then(data => {
            const signed = new Set();
            (data.transactions || []).forEach(txn => {
              const rows = classifyTransactions(txn);
              rows.forEach(row => {
                if (['signed', 'resign', 'trade'].includes(row.type) && row.player) {
                  signed.add(row.player.toLowerCase().trim());
                }
              });
            });
            _signedCache = signed;
            return signed;
          })
          .catch(() => new Set());

    Promise.all([faPromise, txnPromise])
      .then(([players, signed]) => {
        setAll(players);
        setSignedNames(signed);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSort = (key) => {
    setOrder(orderBy === key && order === 'desc' ? 'asc' : 'desc');
    setOrderBy(key);
  };

  const filtered = all
    .filter(p => {
      // Remove players who have been signed or traded per ESPN transactions
      if (!p.name) return true;
      const nameLower = p.name.toLowerCase().trim();
      // Check exact match or if any signed name contains this player's name
      if (signedNames.has(nameLower)) return false;
      // Also check partial match (ESPN sometimes includes suffixes like Jr., III)
      for (const sn of signedNames) {
        if (sn.includes(nameLower) || nameLower.includes(sn)) return false;
      }
      return true;
    })
    .filter(p => (pos === 'ALL' || p.pos === pos))
    .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.lastTeam?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[orderBy] ?? (order === 'desc' ? -Infinity : Infinity);
      const bv = b[orderBy] ?? (order === 'desc' ? -Infinity : Infinity);
      return order === 'desc' ? bv - av : av - bv;
    });

  const SortCell = ({ colKey, label, tip }) => (
    <TableCell align="right" sx={{ ...TH }}>
      <Tooltip title={tip} placement="top" arrow>
        <TableSortLabel active={orderBy===colKey} direction={orderBy===colKey?order:'desc'} onClick={()=>handleSort(colKey)}
          sx={{ '& .MuiTableSortLabel-icon': { color: '#f5a623 !important' }, color: 'inherit' }}>
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  return (
    <Box sx={CARD}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(245,166,35,0.15)', backgroundColor: '#0e0e12', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', color: '#10b981', textTransform: 'uppercase' }}>
          📋 Remaining Free Agents 2026
        </Typography>
        <ToggleButtonGroup value={pos} exclusive onChange={(_,v)=>v&&setPos(v)} size="small">
          {FA_POS.map(g => (
            <ToggleButton key={g} value={g} sx={{
              color: pos===g ? '#10b981' : '#64748b', borderColor: 'rgba(245,166,35,0.2)',
              backgroundColor: pos===g ? 'rgba(16,185,129,0.08)' : 'transparent',
              fontWeight: 800, fontSize: '0.65rem', px: 1.25, py: 0.35,
              '&:hover': { backgroundColor: 'rgba(16,185,129,0.06)' },
            }}>{g}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><span style={{color:'#475569',fontSize:'0.8rem'}}>🔍</span></InputAdornment> }}
          sx={{ width: 160, ml: 'auto', '& .MuiOutlinedInput-root': { backgroundColor: '#0a0a0a', fontSize: '0.8rem' },
            '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(245,166,35,0.2)' },
            '& input': { color: '#ffffff', py: 0.65 } }} />
        <Typography sx={{ fontSize: '0.65rem', color: '#475569' }}>{filtered.length} players</Typography>
      </Box>

      {loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} sx={{ color: '#10b981' }} /></Box>
      ) : (
        <Box sx={{ overflowX: 'auto', ...SCROLLBAR }}>
          <Table size="small" stickyHeader sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, width: 32 }}>#</TableCell>
                <TableCell sx={{ ...TH }}>Player</TableCell>
                <TableCell sx={{ ...TH, width: 40 }}>Pos</TableCell>
                <SortCell colKey="nerdRating" label="NR 2024" tip="Nerd Rating from 2024 season (0–100)" />
                <SortCell colKey="lastApyM"   label="Last APY" tip="Last known average annual value ($M)" />
                <SortCell colKey="lastYear"   label="Exp" tip="Year contract expired" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(0, 200).map((p, i) => (
                <TableRow key={p.name+i} sx={{ '& td': { borderBottom: '1px solid rgba(245,166,35,0.06)' }, '&:hover td': { backgroundColor: 'rgba(245,166,35,0.04)' } }}>
                  <TableCell sx={{ py: 0.5, color: '#475569', fontWeight: 700, fontSize: '0.7rem', textAlign: 'right', backgroundColor: 'transparent' }}>{i+1}</TableCell>
                  <TableCell sx={{ py: 0.5, backgroundColor: 'transparent' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TeamLogo abbr={p.lastTeam} size={20} />
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, backgroundColor: 'transparent' }}>
                    <Chip label={p.pos} size="small" sx={{ height: 17, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.25)' }} />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, backgroundColor: 'transparent', color: p.nerdRating ? '#10b981' : '#334155', fontFamily: 'monospace', fontWeight: 800, fontSize: '0.85rem' }}>
                    {p.nerdRating?.toFixed(1) ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.8rem' }}>
                    {p.lastApyM ? `$${p.lastApyM}M` : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.75rem' }}>
                    {p.lastYear || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid rgba(245,166,35,0.1)', display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#334155' }}>
          Source: nflverse contracts · nflfastR 2024 · Sleeper
        </Typography>
      </Box>
    </Box>
  );
}

// ── Key Dates Banner ──────────────────────────────────────────────────────────
function KeyDatesBanner() {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
      {[
        { label: '🕐 Legal Tampering', date: 'Mar 10, 2026', color: '#f5a623' },
        { label: '🏈 New League Year', date: 'Mar 12, 2026', color: '#10b981' },
        { label: '🚀 FA Opens',        date: 'Mar 12, 2026', color: '#10b981' },
        { label: '🏷️ Franchise Tag Deadline', date: 'Mar 4, 2026', color: '#94a3b8', done: true },
      ].map(d => (
        <Box key={d.label} sx={{ px: 1.75, py: 0.75, backgroundColor: '#131318', border: `1px solid ${d.done ? 'rgba(148,163,184,0.15)' : 'rgba(245,166,35,0.25)'}`, borderRadius: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: d.done ? '#475569' : d.color }}>{d.label}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: d.done ? '#334155' : '#ffffff', fontVariantNumeric: 'tabular-nums' }}>{d.date}</Typography>
          {d.done && <Chip label="DONE" size="small" sx={{ height: 14, fontSize: '0.5rem', fontWeight: 900, bgcolor: '#1c1c24', color: '#475569', ml: 0.5 }} />}
        </Box>
      ))}
    </Box>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────
export default function FreeAgentExplorer() {
  return (
    <Box>
      <KeyDatesBanner />
      <Box sx={{ px: 2, py: 1, mb: 2, backgroundColor: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.4, flexShrink: 0 }}>⚠️</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#94a07a', lineHeight: 1.5 }}>
          <strong style={{ color: '#f5a623' }}>2024 season data — list may include retired players or players who recently signed.</strong> Contract expiry is based on nflverse data which can lag. NR reflects 2024 performance only.
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '460px 1fr' }, gap: 2, alignItems: 'start' }}>
        <RecentMoves />
        <ConsensusBoard />
      </Box>
    </Box>
  );
}
